import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";

import { ReportsViewWidget } from "@/app/app/_/views/ReportsView/ReportsViewWidget/ReportsViewWidget";
import {
  PRESENTATION_LABELS,
  type PresentationKind,
  type ReportLayoutWidget,
} from "@/app/app/_/views/ReportsView/ReportsView.utils";
import { ReportWidgetBody } from "@/components/organisms/ReportWidget/ReportWidgetBody";
import { REPORT_WIDGET_CONTENT_QUERY } from "@/app/app/_/views/ReportsView/reportWidgetQueries";
import { useShareSessionToken } from "@/app/share/report/ShareSessionContext";
import type { WidgetFilterSets } from "@/app/app/_/views/ReportsView/reportFilters";
import type { SharedViewWidgetShell } from "@/app/share/report/sharedReportTypes";
import { resolveSharedWidgetPresentation } from "@/app/share/report/sharedReportToDefinition";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { formatApolloError } from "@/utils/graphqlErrors";
import { Typography } from "@/components/ui/typography";

/** What a widget says it is, for when there is nothing to draw. */
function widgetSummary(widget: SharedViewWidgetShell): string {
  if (!widget.source) {
    return widget.title?.trim() || "Widget";
  }
  const source = widget.source
    .replace(/_/g, " ")
    .replace(/^./, (first) => first.toUpperCase());
  if (!widget.presentation) return source;
  const presentation =
    PRESENTATION_LABELS[widget.presentation as PresentationKind] ??
    widget.presentation;
  return `${source} · ${presentation}`;
}

function reportLayoutWidgetForSkeleton(
  shell: SharedViewWidgetShell,
  reportLayoutWidget?: ReportLayoutWidget,
): ReportLayoutWidget {
  if (reportLayoutWidget) return reportLayoutWidget;
  return {
    id: shell.identifier ?? "shared",
    source: shell.source ?? undefined,
    presentation: resolveSharedWidgetPresentation(shell, reportLayoutWidget),
  };
}

export function SharedWidgetBody({
  widget,
  identifier,
  filterSet,
  reportLayoutWidget,
}: {
  widget: SharedViewWidgetShell;
  identifier: string;
  filterSet?: WidgetFilterSets | null;
  reportLayoutWidget?: ReportLayoutWidget;
}) {
  const sessionToken = useShareSessionToken();
  const nodeId = useMemo(
    () => identifierAndTypenameToId(identifier, "ReportWidgetObject"),
    [identifier],
  );
  const resolvedFilterSets = useMemo<WidgetFilterSets | undefined>(
    () => filterSet ?? undefined,
    [filterSet],
  );

  const { data, loading, error } = useQuery(REPORT_WIDGET_CONTENT_QUERY, {
    variables: {
      id: nodeId,
      ...resolvedFilterSets,
    },
    skip: !sessionToken || !identifier,
    fetchPolicy: "network-only",
    context: sessionToken
      ? {
          headers: {
            Authorization: `ShareSession ${sessionToken}`,
          },
        }
      : undefined,
  });

  if (loading && !data) {
    return (
      <div className="flex-1 min-h-0 flex flex-col">
        <ReportsViewWidget
          widget={reportLayoutWidgetForSkeleton(widget, reportLayoutWidget)}
          reportIdentifier={null}
          layoutEntityIdentifiers={[]}
          isLoading
        />
      </div>
    );
  }

  if (error) {
    return (
      <Typography level="body-sm" className="text-destructive">
        {formatApolloError(error)}
      </Typography>
    );
  }

  const content =
    data?.node?.__typename === "ReportWidgetObject" ? data.node : null;
  if (!content?.data) {
    return (
      <Typography level="body-sm" className="text-muted-foreground/80">
        {widgetSummary(widget)}
      </Typography>
    );
  }

  const presentation = resolveSharedWidgetPresentation(
    widget,
    reportLayoutWidget,
  );
  const body = (
    <ReportWidgetBody
      data={content.data}
      presentation={presentation}
      fallbackLabel={widgetSummary(widget)}
      rows={widget.source?.replace(/_/g, " ")}
    />
  );

  if (body != null) {
    return body;
  }

  return (
    <Typography level="body-sm" className="text-muted-foreground/80">
      {widgetSummary(widget)}
    </Typography>
  );
}
